import React from 'react';
import { ActionMenu, ActionMenuItem } from './ui/ActionMenu';

interface RowActionsProps {
  onEdit: () => void;
  onClone?: () => void;
  onTogglePause?: () => void;
  isPaused?: boolean;
  onDelete: () => void;
  /** The row's subject, so the menu's accessible name says which row it
   *  belongs to rather than "More actions" twenty-seven times. */
  label?: string;
}

/**
 * Every row-level action, in one menu.
 *
 * Edit used to sit beside the menu as a text button. Once a row opens its own
 * edit screen on click, that button is the same click twice — and a column of
 * twenty-seven accent-coloured "Edit" links is a lot of weight spent saying
 * what the whole row already does. Filled buttons stay reserved for page
 * headers and form footers, so lists stay quiet.
 *
 * One implementation at every width: the old component had a separate desktop
 * button row and mobile menu, which drifted apart.
 */
export const RowActions: React.FC<RowActionsProps> = ({
  onEdit,
  onClone,
  onTogglePause,
  isPaused,
  onDelete,
  label,
}) => {
  const items: ActionMenuItem[] = [{ label: 'Edit', onSelect: onEdit }];
  if (onClone) items.push({ label: 'Clone', onSelect: onClone });
  if (onTogglePause) {
    items.push({ label: isPaused ? 'Activate' : 'Pause', onSelect: onTogglePause });
  }
  items.push({ label: 'Delete', onSelect: onDelete, danger: true });

  return <ActionMenu items={items} aria-label={label ? `Actions for ${label}` : undefined} />;
};

/**
 * Wraps a row's action cell so a click there does not also trigger the row's
 * own click-through. Without it, opening the menu opens the edit screen
 * underneath it.
 */
export const RowActionCell: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={className}
    onClick={e => e.stopPropagation()}
    onKeyDown={e => e.stopPropagation()}
  >
    {children}
  </div>
);
